import React from 'react';
import { User } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ProfileAvatarProps {
  src?: string | null;
  alt?: string;
  displayName?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}

const sizeClasses = {
  sm: 'h-12 w-12 text-lg',
  md: 'h-16 w-16 text-2xl',
  lg: 'h-24 w-24 text-4xl',
  xl: 'h-32 w-32 text-5xl',
};

export const ProfileAvatar: React.FC<ProfileAvatarProps> = ({
  src,
  alt,
  displayName,
  size = 'xl',
  className,
}) => {
  const getInitials = () => {
    if (!displayName || typeof displayName !== 'string') return null;
    const parts = displayName.trim().split(/\s+/);
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return displayName.slice(0, 2).toUpperCase();
  };

  const initials = getInitials();
  const sizeCls = sizeClasses[size];

  if (src) {
    return (
      <div
        className={cn(
          'relative overflow-hidden rounded-full ring-4 ring-white/20 shadow-xl dark:ring-zinc-800/50',
          'bg-muted',
          sizeCls,
          className
        )}
      >
        <img
          src={src}
          alt={alt ?? displayName ?? 'Profile'}
          className="h-full w-full object-cover"
        />
      </div>
    );
  }

  return (
    <div
      className={cn(
        'relative flex items-center justify-center rounded-full',
        'bg-gradient-to-br from-violet-500 via-fuchsia-500 to-amber-500',
        'ring-4 ring-white/30 dark:ring-zinc-800/60',
        'shadow-xl shadow-violet-500/25 dark:shadow-violet-900/30',
        'text-white font-semibold',
        sizeCls,
        className
      )}
      title="Profile photo"
    >
      {initials ? (
        <span className="select-none">{initials}</span>
      ) : (
        <User className="h-[50%] w-[50%] opacity-90" strokeWidth={1.5} />
      )}
    </div>
  );
};
